/**
 * Simple raster annotator for note attachments — pen strokes + text labels.
 *
 * Editing model: the base image never changes. Annotations are kept as a
 * VECTOR op list (`Op[]`) and replayed onto the canvas on every redraw, which
 * is what makes undo/redo cheap and exact — no ImageData snapshots, no
 * accumulated resampling. The ops are flattened into the PNG only at save
 * time, so an edit is not re-editable afterwards; that is the deliberate
 * trade (see features/notes.md).
 *
 * Coordinates are IMAGE pixels throughout. The canvas backing store is the
 * image's natural size and CSS scales it down to fit, so `toImagePoint`
 * converting via `getBoundingClientRect` is the only mapping needed — and
 * devicePixelRatio never enters into it, because we are not trying to match
 * device pixels, only image pixels.
 *
 * ⚠ The source PNG is fetched as a blob and drawn from an object URL rather
 * than pointed at with `<img src={apiUrl(...)}>`. In production the API is a
 * DIFFERENT ORIGIN (api.pulsarix.net — see VITE_API_BASE), so a direct
 * cross-origin <img> would taint the canvas and make `toBlob()` throw
 * SecurityError. It would have worked in local dev and failed only in prod.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styles from "./ImageEditor.module.css";

type Point = { x: number; y: number };

type PenOp = {
  kind: "pen";
  color: string;
  width: number;
  points: Point[];
};

type TextOp = {
  kind: "text";
  color: string;
  size: number;
  /** Baseline-left anchor, in image pixels. */
  x: number;
  y: number;
  text: string;
};

/** Arrow, rectangle and ellipse all reduce to the same thing: two
 *  corners of a drag. Kept as one op type rather than three so the
 *  drag, the preview and the hit-free "not movable" story are shared. */
type ShapeOp = {
  kind: "arrow" | "rect" | "ellipse";
  color: string;
  width: number;
  /** Drag start and end, in image pixels. For rect/ellipse these are
   *  opposite corners of the bounding box and either order is fine;
   *  for an arrow the direction matters — the head goes at `to`. */
  from: Point;
  to: Point;
};

type Op = PenOp | TextOp | ShapeOp;

type Tool = "pen" | "text" | ShapeOp["kind"];

const SHAPE_TOOLS: ShapeOp["kind"][] = ["arrow", "rect", "ellipse"];

const SHAPE_LABELS: Record<
  ShapeOp["kind"],
  { glyph: string; name: string; title: string }
> = {
  arrow: {
    glyph: "↗",
    name: "Arrow",
    title: "Arrow — drag from the tail to the head",
  },
  rect: {
    glyph: "▭",
    name: "Rect",
    title: "Rectangle — drag a box; hold Shift for a square",
  },
  ellipse: {
    glyph: "◯",
    name: "Circle",
    title: "Ellipse — drag a box; hold Shift for a true circle",
  },
};

function isShapeTool(t: Tool): t is ShapeOp["kind"] {
  return (SHAPE_TOOLS as string[]).includes(t);
}

/** Drag shorter than this (image px, before the size factor) is treated
 *  as a stray click and discarded. Without it a click with a shape tool
 *  selected leaves a zero-size op that can't be seen or clicked, only
 *  undone. */
const MIN_SHAPE_DRAG = 4;

/** Matches the server's own cap (`MAX_ATTACHMENT_BYTES` in api/notes.py) so an
 *  oversized export is reported here instead of as a 413 after the round trip. */
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

const COLORS = [
  "#ff5b6b",
  "#f4c44c",
  "#23d5a4",
  "#3d7bff",
  "#9b7cff",
  "#ffffff",
  "#101319",
];

/** Stroke widths, shared by the pen and the shape tools — it is one
 *  "how thick is the line" choice and splitting it just meant setting
 *  it twice. */
const PEN_WIDTHS = [2, 4, 8, 16];
/** Notches, not literal pixels — each is multiplied by `sizeFactor` so a given
 *  choice looks the same on screen whatever the source resolution. */
const TEXT_SIZES = [8, 12, 16, 24];

export interface ImageEditorProps {
  /** URL of the PNG to annotate. Fetched as a blob (see the CORS note above). */
  src: string;
  /** Original filename, used to derive the saved one. */
  filename: string;
  onCancel: () => void;
  /** Receives the flattened PNG. May be async; the editor shows a busy state
   *  and stays mounted until it resolves so a failure can surface here. */
  onSave: (blob: Blob, filename: string) => Promise<void> | void;
}

/** `chart.png` -> `chart-edited.png`, but re-editing stays `chart-edited.png`
 *  rather than growing a `-edited-edited-…` tail. */
function editedFilename(original: string): string {
  const dot = original.lastIndexOf(".");
  const stem = dot > 0 ? original.slice(0, dot) : original;
  return `${stem.endsWith("-edited") ? stem : `${stem}-edited`}.png`;
}

/** Snap a drag to a square bounding box, for shift-constrain. Uses the
 *  larger of the two deltas so the shape follows the pointer rather
 *  than collapsing toward the smaller axis, and keeps each sign so it
 *  can still be dragged up or left. */
function shapeSpan(sh: ShapeOp): number {
  return Math.hypot(sh.to.x - sh.from.x, sh.to.y - sh.from.y);
}

function squared(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return {
    x: from.x + Math.sign(dx || 1) * side,
    y: from.y + Math.sign(dy || 1) * side,
  };
}

/** Arrow / rectangle / ellipse. Pure — takes only a context and an op —
 *  so it serves the committed replay and the in-progress preview alike. */
function paintShape(ctx: CanvasRenderingContext2D, op: ShapeOp): void {
  ctx.strokeStyle = op.color;
  ctx.fillStyle = op.color;
  ctx.lineWidth = op.width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (op.kind === "arrow") {
    const angle = Math.atan2(op.to.y - op.from.y, op.to.x - op.from.x);
    // Head scales with the stroke, or a thin arrow gets a head too small
    // to read as one on a 2500px screenshot.
    const head = op.width * 4;
    const spread = 0.42;
    // Stop the shaft short of the tip. Running it all the way would let
    // a round line cap poke out through the point of the head.
    const backX = op.to.x - Math.cos(angle) * head * 0.85;
    const backY = op.to.y - Math.sin(angle) * head * 0.85;
    ctx.beginPath();
    ctx.moveTo(op.from.x, op.from.y);
    ctx.lineTo(backX, backY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(op.to.x, op.to.y);
    ctx.lineTo(
      op.to.x - Math.cos(angle - spread) * head,
      op.to.y - Math.sin(angle - spread) * head,
    );
    ctx.lineTo(
      op.to.x - Math.cos(angle + spread) * head,
      op.to.y - Math.sin(angle + spread) * head,
    );
    ctx.closePath();
    ctx.fill();
    return;
  }

  const x = Math.min(op.from.x, op.to.x);
  const y = Math.min(op.from.y, op.to.y);
  const w = Math.abs(op.to.x - op.from.x);
  const h = Math.abs(op.to.y - op.from.y);

  if (op.kind === "rect") {
    ctx.strokeRect(x, y, w, h);
    return;
  }
  // Inscribed in the drag box, which is what every annotation tool
  // does — "circle" in the toolbar, an ellipse unless you hold shift.
  ctx.beginPath();
  ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  ctx.stroke();
}

export function ImageEditor({
  src,
  filename,
  onCancel,
  onSave,
}: ImageEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseRef = useRef<HTMLImageElement | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Annotations are drawn in IMAGE pixels, but the user picks a size while
  // looking at a scaled-down view. A chart screenshot is routinely 2500px+
  // wide, where a literal 24px label is an unreadable speck. Scale the menu
  // values against a 1000px reference so a given choice looks the same on
  // screen whatever the source resolution. Never below 1 — small images must
  // not get hairline text.
  const [sizeFactor, setSizeFactor] = useState(1);

  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [penWidth, setPenWidth] = useState(4);
  const [textSize, setTextSize] = useState(12);

  // Committed ops + the redo stack. `useState` (not a ref) so the toolbar's
  // enabled/disabled state tracks them.
  const [ops, setOps] = useState<Op[]>([]);
  const [redo, setRedo] = useState<Op[]>([]);

  // In-progress pen stroke. A ref, not state: pointermove fires far faster
  // than we want to re-render React, and the segment is painted directly.
  const drawingRef = useRef<PenOp | null>(null);

  // In-progress shape, same reasoning. Unlike a stroke it cannot be
  // painted incrementally — the whole shape changes as you drag — so the
  // preview replays the canvas, coalesced onto animation frames by
  // `previewRaf`. Without that coalescing a pointermove storm would
  // redraw a 2500px base image dozens of times per frame.
  const shapeRef = useRef<ShapeOp | null>(null);
  const previewRaf = useRef(0);

  // Two-click shape waiting for its second click. A click that doesn't
  // travel far enough to be a drag arms this instead of being thrown
  // away, so both gestures fall out of one pointerdown with no mode to
  // choose. The boolean mirrors it for the hint and the Escape handler,
  // which are the only things that render off it — pointermove must not
  // touch React state.
  const pendingShapeRef = useRef<ShapeOp | null>(null);
  const [shapeArmed, setShapeArmed] = useState(false);

  // Pending text box, anchored in image coordinates.
  //
  // Mirrored into a ref because `commitText` is reachable from three handlers
  // that can fire within one event (input blur, then the canvas pointerdown
  // that caused it). Reading component state there gives every one of them the
  // SAME pre-blur closure, so the label gets committed once per handler. The
  // ref is cleared synchronously, so later callers in the same tick correctly
  // see "nothing pending".
  type Pending = {
    /** Identity of this box, NOT its position — dragging changes x/y on every
     *  frame, and keying the focus effect off those would re-focus (and fight
     *  the drag) continuously. */
    id: number;
    x: number;
    y: number;
    value: string;
    /** Carried on the box rather than read from the toolbar at commit time, so
     *  a label picked back up keeps the colour/size it was created with. */
    color: string;
    /** Final image-pixel size, i.e. already multiplied by `sizeFactor`. */
    size: number;
  };
  const pendingRef = useRef<Pending | null>(null);
  const [pendingText, setPendingTextState] = useState<Pending | null>(null);
  const setPendingText = useCallback((v: Pending | null) => {
    pendingRef.current = v;
    setPendingTextState(v);
  }, []);
  const nextBoxId = useRef(0);
  const textInputRef = useRef<HTMLInputElement>(null);
  /** Grab offset while dragging the box, in image px. */
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  // ── Load the base image ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        // credentials: attachments are owner-gated, same as every other call.
        //
        // cache: "no-store" is LOAD-BEARING, not a tidiness flag. The
        // thumbnail <img> has already loaded this exact URL in *no-cors* mode,
        // which stores an OPAQUE response in the HTTP cache. A later fetch() —
        // a *cors*-mode request — that reuses that entry sees a response with
        // no Access-Control-Allow-Origin and fails as a CORS error WITHOUT
        // ever reaching the server (the access log shows no request at all).
        // Bypassing the cache forces a real CORS round trip. The blob is
        // served `private, max-age=3600`, so this costs one re-download when
        // the editor opens — a deliberate user action, worth it.
        const resp = await fetch(src, {
          credentials: "include",
          cache: "no-store",
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("decode failed"));
          img.src = objectUrl;
        });
        // Decoded — the URL has done its job. Revoking here (rather than only
        // in cleanup) means an unmount mid-load can't strand it, which
        // StrictMode's double-mount would otherwise hit every time.
        URL.revokeObjectURL(objectUrl);
        objectUrl = "";
        if (cancelled) return;
        baseRef.current = img;
        setSizeFactor(Math.max(1, (img.naturalWidth || 1000) / 1000));
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext("2d")?.drawImage(img, 0, 0);
        }
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "could not load image",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  // ── Rendering ──────────────────────────────────────────────────────
  const paintOp = useCallback((ctx: CanvasRenderingContext2D, op: Op) => {
    if (op.kind === "pen") {
      if (op.points.length === 0) return;
      ctx.strokeStyle = op.color;
      ctx.lineWidth = op.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(op.points[0].x, op.points[0].y);
      // A single tap has no segment to stroke — draw a dot so a click still
      // leaves a mark.
      if (op.points.length === 1) {
        ctx.lineTo(op.points[0].x + 0.01, op.points[0].y);
      } else {
        for (let i = 1; i < op.points.length; i++) {
          ctx.lineTo(op.points[i].x, op.points[i].y);
        }
      }
      ctx.stroke();
      return;
    }
    if (op.kind !== "text") {
      paintShape(ctx, op);
      return;
    }
    ctx.fillStyle = op.color;
    // Plain `sans-serif` on purpose. `ctx.font` silently IGNORES a declaration
    // it cannot parse, leaving the 10px default — so one unsupported generic
    // (`ui-sans-serif`) anywhere in the stack renders every label as
    // near-invisible specks with no error. Not worth the nicer typeface.
    ctx.font = `600 ${op.size}px sans-serif`;
    ctx.textBaseline = "top";
    ctx.fillText(op.text, op.x, op.y);
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const base = baseRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !base || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(base, 0, 0);
    for (const op of ops) paintOp(ctx, op);
    // The shape being dragged, or the one waiting for its second click,
    // is painted here rather than by the drag handler. That keeps every
    // repaint correct for free — an undo mid-gesture redraws through
    // this path and the rubber band survives it.
    const active = shapeRef.current ?? pendingShapeRef.current;
    if (active && shapeSpan(active) >= MIN_SHAPE_DRAG * sizeFactor) {
      paintShape(ctx, active);
    }
  }, [ops, paintOp, sizeFactor]);

  useEffect(() => {
    if (ready) redraw();
  }, [ready, redraw]);

  // A shape drag can be interrupted by an unmount — closing the editor
  // mid-drag, or StrictMode's double-mount — leaving a frame queued
  // against a canvas that no longer exists.
  useEffect(
    () => () => {
      if (previewRaf.current) cancelAnimationFrame(previewRaf.current);
    },
    [],
  );

  // ── Pointer -> image coordinates ───────────────────────────────────
  // Takes bare client coords so it works for events on the drag grip too, not
  // only for events targeted at the canvas.
  const toImagePoint = (e: { clientX: number; clientY: number }): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  /** Topmost committed label under `p`, or null.
   *
   *  Bounding box from `measureText` with the op's own font — measurement is a
   *  read, so borrowing the live context is safe (redraw sets `font` per op
   *  anyway). Walks back-to-front so the label drawn last wins, matching what
   *  the user sees. */
  const hitTestText = (p: Point): TextOp | null => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return null;
    for (let i = ops.length - 1; i >= 0; i--) {
      const op = ops[i];
      if (op.kind !== "text") continue;
      ctx.font = `600 ${op.size}px sans-serif`;
      const w = ctx.measureText(op.text).width;
      // textBaseline is "top", so y IS the top edge; 1.2em approximates the
      // line box closely enough for a grab target.
      const h = op.size * 1.2;
      if (p.x >= op.x && p.x <= op.x + w && p.y >= op.y && p.y <= op.y + h) {
        return op;
      }
    }
    return null;
  };

  /** Image px -> CSS px, for sizing the text overlay so it is WYSIWYG. */
  const displayScale = (): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 1;
    const rect = canvas.getBoundingClientRect();
    return rect.width / canvas.width || 1;
  };

  // ── Pen ────────────────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!ready || saving) return;
    if (tool === "text") {
      // Suppress the default focus shift. The canvas isn't focusable, so the
      // click would otherwise pull focus to <body> and blur the box we are
      // about to open.
      e.preventDefault();
      // Commit any open box first, then open a new one where they clicked.
      commitText();
      const p = toImagePoint(e);
      // Clicking an existing label picks it back up rather than stacking a new
      // one on top. Committed labels are still vector ops (nothing is baked
      // until save), so "pick up" is just: pull the op out of the list and
      // re-open it as the pending box.
      const hit = hitTestText(p);
      if (hit) {
        // Filter by identity, not index: the commitText above may have
        // appended, and ops are never mutated in place.
        setOps((prev) => prev.filter((o) => o !== hit));
        setPendingText({
          id: nextBoxId.current++,
          x: hit.x,
          y: hit.y,
          value: hit.text,
          color: hit.color,
          size: hit.size,
        });
        return;
      }
      setPendingText({
        id: nextBoxId.current++,
        x: p.x,
        y: p.y,
        value: "",
        color,
        size: textSize * sizeFactor,
      });
      return;
    }
    const armed = pendingShapeRef.current;
    if (armed) {
      // Second click of a two-click shape. No pointer capture and no
      // drag state — this click ends the gesture either way.
      const p = toImagePoint(e);
      armed.to = e.shiftKey && armed.kind !== "arrow" ? squared(armed.from, p) : p;
      pendingShapeRef.current = null;
      setShapeArmed(false);
      if (shapeSpan(armed) >= MIN_SHAPE_DRAG * sizeFactor) {
        setOps((prev) => [...prev, armed]);
        setRedo([]);
      } else {
        // Clicked the anchor again. Treated as "changed my mind" rather
        // than staying armed, which would feel stuck.
        redraw();
      }
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toImagePoint(e);
    // Scaled like the text size, and for the same reason: a literal 4px
    // line on a 2500px screenshot displays as a hairline. This used to
    // be raw `penWidth`, which is why the pen read so thin on a big
    // capture while labels looked right.
    const width = penWidth * sizeFactor;
    if (isShapeTool(tool)) {
      shapeRef.current = { kind: tool, color, width, from: p, to: p };
      return;
    }
    drawingRef.current = { kind: "pen", color, width, points: [p] };
  };

  /** Repaint, at most once per animation frame. A shape can't be painted
   *  incrementally the way a stroke can — it changes shape as the pointer
   *  moves — so each update is a full replay, and a 2500px base image
   *  can't take one per pointermove event. */
  const schedulePreview = () => {
    if (previewRaf.current) return;
    previewRaf.current = requestAnimationFrame(() => {
      previewRaf.current = 0;
      redraw();
    });
  };

  /** Abandon a shape mid-gesture — Escape, or switching tool. */
  const cancelShape = useCallback(() => {
    if (!shapeRef.current && !pendingShapeRef.current) return false;
    shapeRef.current = null;
    pendingShapeRef.current = null;
    setShapeArmed(false);
    redraw();
    return true;
  }, [redraw]);

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const shape = shapeRef.current ?? pendingShapeRef.current;
    if (shape) {
      const p = toImagePoint(e);
      // Shift constrains the box to a square, so a rectangle boxes a level
      // squarely and an ellipse becomes a true circle. Mutating the ref is
      // deliberate — nothing renders off it but the preview.
      shape.to =
        e.shiftKey && shape.kind !== "arrow" ? squared(shape.from, p) : p;
      schedulePreview();
      return;
    }
    const stroke = drawingRef.current;
    if (!stroke) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const p = toImagePoint(e);
    const prev = stroke.points[stroke.points.length - 1];
    stroke.points.push(p);
    // Paint just the new segment — a full replay on every move would get
    // slow on a large screenshot.
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const endStroke = () => {
    if (previewRaf.current) {
      cancelAnimationFrame(previewRaf.current);
      previewRaf.current = 0;
    }
    const shape = shapeRef.current;
    shapeRef.current = null;
    if (shape) {
      const far =
        Math.hypot(shape.to.x - shape.from.x, shape.to.y - shape.from.y) >=
        MIN_SHAPE_DRAG * sizeFactor;
      if (!far) {
        // Not a drag — so treat it as the FIRST of two clicks rather than
        // throwing it away. The pointer is released, the anchor stays,
        // and the next move rubber-bands from it. One pointerdown serves
        // both gestures with no mode for the user to pick.
        shape.to = shape.from;
        pendingShapeRef.current = shape;
        setShapeArmed(true);
        redraw();
        return;
      }
      setOps((prev) => [...prev, shape]);
      setRedo([]);
      return;
    }
    const stroke = drawingRef.current;
    drawingRef.current = null;
    if (!stroke) return;
    setOps((prev) => [...prev, stroke]);
    setRedo([]);
  };

  // ── Text ───────────────────────────────────────────────────────────
  /** Commit the open label (if it has content) and close the box. */
  const commitText = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    setPendingText(null);
    if (!pending.value.trim()) return;
    // Not a setState updater: React runs updaters twice under StrictMode, and
    // a nested setOps inside one would commit every label twice.
    setOps((prev) => [
      ...prev,
      {
        kind: "text",
        color: pending.color,
        size: pending.size,
        x: pending.x,
        y: pending.y,
        text: pending.value,
      },
    ]);
    setRedo([]);
  }, [setPendingText]);

  /** Blur handler. Deliberately NOT `commitText`: an EMPTY box must survive a
   *  blur. The click that opens the box also moves focus off the canvas, so
   *  blur fires immediately on the freshly-focused input — and closing on that
   *  made the box appear and vanish before anything could be typed. */
  const onTextBlur = useCallback(() => {
    if (pendingRef.current?.value.trim()) commitText();
  }, [commitText]);

  // Layout effect, not passive: focus must land before the browser paints and
  // before the originating click finishes, or the caret never reaches the box.
  // Keyed on the box's id, so neither typing nor dragging re-focuses.
  useLayoutEffect(() => {
    if (pendingRef.current) textInputRef.current?.focus();
  }, [pendingText?.id]);

  // ── Dragging the open box ──────────────────────────────────────────
  const onGripDown = (e: React.PointerEvent<HTMLElement>) => {
    const pending = pendingRef.current;
    if (!pending) return;
    // stopPropagation so the canvas doesn't also treat this as "place a label
    // here"; preventDefault so the grab doesn't blur the input.
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toImagePoint(e);
    dragRef.current = { dx: p.x - pending.x, dy: p.y - pending.y };
  };

  const onGripMove = (e: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    const pending = pendingRef.current;
    if (!drag || !pending) return;
    const p = toImagePoint(e);
    setPendingText({ ...pending, x: p.x - drag.dx, y: p.y - drag.dy });
  };

  const onGripUp = (e: React.PointerEvent<HTMLElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* capture already gone */
    }
    // Put the caret back so typing continues straight after a reposition.
    textInputRef.current?.focus();
  };

  // ── History ────────────────────────────────────────────────────────
  const undo = useCallback(() => {
    setOps((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedo((r) => [...r, last]);
      return prev.slice(0, -1);
    });
  }, []);

  const redoLast = useCallback(() => {
    setRedo((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setOps((o) => [...o, last]);
      return prev.slice(0, -1);
    });
  }, []);

  const reset = () => {
    setOps([]);
    setRedo([]);
    setPendingText(null);
    // Includes a shape mid-gesture, which is otherwise left armed and
    // would rubber-band out of a canvas that has just been cleared.
    cancelShape();
  };

  // ── Save ───────────────────────────────────────────────────────────
  /** Render the export off-screen from the op list rather than reading the
   *  visible canvas. Two things fall out of that: an open text box is folded
   *  in instead of being silently dropped, and the output can't inherit a
   *  half-painted in-progress stroke from the live canvas. */
  const exportCanvas = (): HTMLCanvasElement | null => {
    const base = baseRef.current;
    if (!base) return null;
    const out = document.createElement("canvas");
    out.width = base.naturalWidth;
    out.height = base.naturalHeight;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(base, 0, 0);
    for (const op of ops) paintOp(ctx, op);
    if (pendingText?.value.trim()) {
      paintOp(ctx, {
        kind: "text",
        color: pendingText.color,
        size: pendingText.size,
        x: pendingText.x,
        y: pendingText.y,
        text: pendingText.value,
      });
    }
    return out;
  };

  const save = async () => {
    if (saving) return;
    const canvas = exportCanvas();
    if (!canvas) return;
    setSaving(true);
    setSaveError(null);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png"),
      );
      if (!blob) throw new Error("could not encode the image");
      if (blob.size > MAX_ATTACHMENT_BYTES) {
        throw new Error(
          `Edited image is ${(blob.size / 1024 / 1024).toFixed(1)} MB, over ` +
            `the ${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB attachment limit.`,
        );
      }
      await onSave(blob, editedFilename(filename));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "save failed");
      setSaving(false);
    }
  };

  // ── Keyboard ───────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Let the text overlay own its own keystrokes.
      if (pendingText) {
        // Commit, don't discard. A box holding a label PICKED UP from the
        // canvas has been removed from `ops`, so throwing the box away would
        // silently delete that label. commitText does the right thing either
        // way: empty box just closes, one with content is put back.
        if (e.key === "Escape") {
          // Also swallowed — see the Escape branch below. The
          // surfaces underneath must not close because a text box
          // was dismissed.
          e.stopPropagation();
          commitText();
        }
        return;
      }
      if (e.key === "Escape") {
        // Capture phase + stopPropagation: the Notes / Journal
        // drawers and the Image Board all close themselves on a
        // bubbling Escape, so without this one Escape would dismiss
        // the annotator AND whatever opened it.
        e.stopPropagation();
        // Abandon a shape in progress before closing anything. Escape
        // out of a half-drawn arrow should lose the arrow, not the
        // editor and every annotation in it.
        if (cancelShape()) return;
        onCancel();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redoLast();
        else undo();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel, undo, redoLast, pendingText, cancelShape]);

  const scale = displayScale();

  return (
    <div className={styles.backdrop} role="dialog" aria-label="Edit image">
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.toolbar}>
          <div className={styles.group}>
            <button
              type="button"
              className={`${styles.tool} ${tool === "pen" ? styles.toolActive : ""}`}
              onClick={() => {
                commitText();
                cancelShape();
                setTool("pen");
              }}
              title="Pen — draw freehand"
            >
              ✏️ Pen
            </button>
            <button
              type="button"
              className={`${styles.tool} ${tool === "text" ? styles.toolActive : ""}`}
              onClick={() => {
                cancelShape();
                setTool("text");
              }}
              title="Text — click the image to place a label"
            >
              T Text
            </button>
            {SHAPE_TOOLS.map((k) => (
              <button
                key={k}
                type="button"
                className={`${styles.tool} ${tool === k ? styles.toolActive : ""}`}
                onClick={() => {
                  // Same as switching to the pen: an open label would
                  // otherwise be stranded when the canvas stops handling
                  // clicks as text placement.
                  commitText();
                  cancelShape();
                  setTool(k);
                }}
                title={SHAPE_LABELS[k].title}
              >
                {SHAPE_LABELS[k].glyph} {SHAPE_LABELS[k].name}
              </button>
            ))}
          </div>

          <div className={styles.group}>
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`${styles.swatch} ${color === c ? styles.swatchActive : ""}`}
                style={{ background: c }}
                onClick={() => {
                  setColor(c);
                  // Restyle the open box live, so a picked-up label can be
                  // recoloured without retyping it.
                  const p = pendingRef.current;
                  if (p) setPendingText({ ...p, color: c });
                }}
                title={c}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>

          <div className={styles.group}>
            {(tool === "text" ? TEXT_SIZES : PEN_WIDTHS).map((n) => (
              <button
                key={n}
                type="button"
                className={`${styles.size} ${
                  (tool === "text" ? textSize : penWidth) === n
                    ? styles.sizeActive
                    : ""
                }`}
                onClick={() => {
                  if (tool !== "text") {
                    setPenWidth(n);
                    return;
                  }
                  setTextSize(n);
                  const p = pendingRef.current;
                  if (p) setPendingText({ ...p, size: n * sizeFactor });
                }}
                title={tool === "text" ? `${n}px text` : `${n}px stroke`}
              >
                {tool !== "text" ? (
                  <span
                    className={styles.dot}
                    style={{
                      width: Math.min(n, 14),
                      height: Math.min(n, 14),
                      background: color,
                    }}
                  />
                ) : (
                  n
                )}
              </button>
            ))}
          </div>

          <div className={styles.group}>
            <button
              type="button"
              className={styles.tool}
              onClick={undo}
              disabled={ops.length === 0}
              title="Undo (Ctrl+Z)"
            >
              ↶
            </button>
            <button
              type="button"
              className={styles.tool}
              onClick={redoLast}
              disabled={redo.length === 0}
              title="Redo (Ctrl+Shift+Z)"
            >
              ↷
            </button>
            <button
              type="button"
              className={styles.tool}
              onClick={reset}
              disabled={ops.length === 0}
              title="Remove every annotation"
            >
              Reset
            </button>
          </div>

          <div className={styles.spacer} />

          <div className={styles.group}>
            <button
              type="button"
              className={styles.cancel}
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.save}
              onClick={save}
              disabled={!ready || saving}
              title="Save as a new attachment"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {saveError && <div className={styles.error}>{saveError}</div>}
        {loadError && (
          <div className={styles.error}>
            Could not load the image ({loadError}).
          </div>
        )}

        <div className={styles.stage}>
          <div className={styles.canvasWrap}>
            <canvas
              ref={canvasRef}
              className={styles.canvas}
              style={{ cursor: tool === "text" ? "text" : "crosshair" }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endStroke}
              onPointerCancel={endStroke}
            />
            {pendingText && (
              <div
                className={styles.textBox}
                style={{
                  left: pendingText.x * scale,
                  top: pendingText.y * scale,
                }}
              >
                {/* Sits OUTSIDE the box to the left (right:100%), so the
                    input still starts exactly on the anchor and stays
                    WYSIWYG. Dragging the text itself would fight caret
                    placement, hence a dedicated grip. */}
                <span
                  className={styles.textGrip}
                  onPointerDown={onGripDown}
                  onPointerMove={onGripMove}
                  onPointerUp={onGripUp}
                  onPointerCancel={onGripUp}
                  title="Drag to move this label"
                  aria-label="Move label"
                >
                  ✥
                </span>
                <input
                  ref={textInputRef}
                  className={styles.textOverlay}
                  value={pendingText.value}
                  style={{
                    // The box's own size, mapped back to CSS px — what you
                    // type is what gets drawn.
                    fontSize: pendingText.size * scale,
                    color: pendingText.color,
                  }}
                  onChange={(e) =>
                    setPendingText({ ...pendingText, value: e.target.value })
                  }
                  onBlur={onTextBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitText();
                    }
                  }}
                  placeholder="Type, then Enter"
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.hint}>
          {tool === "pen" && "Drag to draw. Ctrl+Z undoes."}
          {tool === "text" &&
            "Click to place a label, type, Enter to commit. Drag the ✥ grip " +
              "to move it — and click any label you've placed to pick it back " +
              "up and move, retype or recolour it."}
          {isShapeTool(tool) &&
            (shapeArmed
              ? "Click again to finish — Esc cancels."
              : `Drag, or click once and click again to finish.${
                  tool === "arrow"
                    ? " From the tail to the head."
                    : ` Hold Shift for a ${
                        tool === "rect" ? "square" : "circle"
                      }.`
                } Ctrl+Z undoes.`)}
          {" · Saving adds a NEW attachment and removes the original."}
        </div>
      </div>
    </div>
  );
}
